// Shared display formatters. Locales are pinned ("en-CA" / "en-US") so the
// language and shape do not follow the host locale. Instant timestamps still
// use the viewer's local timezone unless a formatter explicitly says otherwise.

const EM_DASH = "—";

/** Money stored as integer cents → "$1,234.56". Negatives → "-$1,234.56". */
export function formatMoney(cents: number, currency = "CAD"): string {
  if (!Number.isFinite(cents)) return EM_DASH;
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
  }).format(cents / 100);
}

export interface FormatPercentOptions {
  /** Max digits after the decimal point. Default 2 (trailing zeros trimmed). */
  maximumFractionDigits?: number;
}

/** Percent value already on a 0–100 scale → "15%" / "15.5%". */
export function formatPercent(
  value: number,
  opts: FormatPercentOptions = {}
): string {
  if (!Number.isFinite(value)) return EM_DASH;
  const formatted = new Intl.NumberFormat("en-CA", {
    maximumFractionDigits: opts.maximumFractionDigits ?? 2,
  }).format(value);
  return `${formatted}%`;
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ISO string → "Jun 26, 2026". Invalid/empty → em dash. */
export function formatDate(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return EM_DASH;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(d);
}

/**
 * ISO calendar date or timestamp → "Jun 26, 2026" without timezone shifting.
 * Use for API values whose first ten characters represent the user-selected
 * calendar day rather than an instant to convert into local time.
 */
export function formatDateOnly(iso: string | null | undefined): string {
  const match = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return EM_DASH;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return EM_DASH;
  }
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/** ISO string → "Jun 26, 2026, 3:45 PM". Invalid/empty → em dash. */
export function formatDateTime(iso: string | null | undefined): string {
  const d = toDate(iso);
  if (!d) return EM_DASH;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** ISO string → "just now" / "3h ago" / "in 2d"; falls back to date past 7d. */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: number = Date.now()
): string {
  const d = toDate(iso);
  if (!d) return EM_DASH;

  const diff = d.getTime() - now;
  const abs = Math.abs(diff);
  if (abs < MINUTE) return "just now";

  const suffix = (n: number, unit: string) =>
    diff < 0 ? `${n}${unit} ago` : `in ${n}${unit}`;

  if (abs < HOUR) return suffix(Math.round(abs / MINUTE), "m");
  if (abs < DAY) return suffix(Math.round(abs / HOUR), "h");
  if (abs < 7 * DAY) return suffix(Math.round(abs / DAY), "d");
  return formatDate(iso);
}
